import {
  ApiError,
  startTextSchema,
  textLength,
  TEXT_MAX_PARTS,
  type Me,
  type TextConversationDetail,
  type TextConversationSummary,
  type TextMessageSummary,
} from '@smarton-pbx/shared';
import { AlertCircle, ArrowLeft, MessageSquare, MessageSquarePlus, PhoneOutgoing, Send, UserPlus } from 'lucide-react';
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../api';
import { Dialog, EmptyState, Field, Loading, Notice, PageHeader } from '../components/ui';
import { locale, message as translated, t, tc, tp } from '../i18n';
import { formatNumber } from '../lib/calls';
import { useLoad, useSubmit } from '../lib/hooks';
import { when } from './Calls';
import { ContactForm } from './contacts';
import { useNotifications } from './notifications';

/** Who the conversation is with: their contact's name, else their number (or the sender's name). */
function who(conversation: TextConversationSummary): string {
  return conversation.contactName ?? formatNumber(conversation.remote);
}

/** Our number, as people know it: its label, else the number. */
function ours(conversation: TextConversationSummary): string {
  return conversation.ourLabel ? `${conversation.ourLabel} · ${formatNumber(conversation.ourNumber)}` : formatNumber(conversation.ourNumber);
}

/** How long a text is: SMS parts, and characters left in the last. */
function Length({ body }: { body: string }) {
  const { parts, left } = textLength(body.trim());
  if (parts === 0) return null;
  return (
    <span className={`text-length ${parts > TEXT_MAX_PARTS ? 'too-long' : ''}`} data-testid="text-length">
      {parts > TEXT_MAX_PARTS ? t('Too long for one text: shorten it') : `${tp(parts, '{n} SMS', '{n} SMS')} · ${tp(left, '{n} character left', '{n} characters left')}`}
    </span>
  );
}

/** The text box: Enter sends, Shift+Enter starts a new line. */
function Composer({ disabled, onSend, autoFocus }: { disabled?: boolean; onSend: (body: string) => Promise<boolean>; autoFocus?: boolean }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const tooLong = textLength(body.trim()).parts > TEXT_MAX_PARTS;

  async function send() {
    if (!body.trim() || busy || tooLong) return;
    setBusy(true);
    if (await onSend(body.trim())) setBody('');
    setBusy(false);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send();
    }
  }

  return (
    <form
      className="text-composer"
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
    >
      <textarea
        rows={2}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t('Write a text…')}
        aria-label={t('Text')}
        disabled={disabled}
        autoFocus={autoFocus}
        data-testid="text-body"
      />
      <div className="row text-composer-foot">
        <Length body={body} />
        <button type="submit" disabled={disabled || busy || !body.trim() || tooLong} data-testid="text-send">
          <Send size={15} aria-hidden /> {t('Send')}
        </button>
      </div>
    </form>
  );
}

function Bubble({ message }: { message: TextMessageSummary }) {
  const time = new Date(message.createdAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  return (
    <div className={`text-bubble ${message.direction === 'IN' ? 'in' : 'out'} ${message.status === 'FAILED' ? 'failed' : ''}`} data-testid="text-message">
      <div className="text-bubble-body">{message.body}</div>
      <div className="text-bubble-meta">
        {message.direction === 'OUT' && message.userName && <>{message.userName} · </>}
        {time}
        {message.status === 'SENDING' && <> · {t('Sending…')}</>}
        {message.status === 'FAILED' && (
          <span className="text-failed">
            {' '}
            · <AlertCircle size={12} aria-hidden /> {t('Not sent')}
            {message.error && <>: {translated(message.error)}</>}
          </span>
        )}
      </div>
    </div>
  );
}

/** Day headings between texts from different days. */
function dayOf(iso: string): string {
  const date = new Date(iso);
  if (date.toDateString() === new Date().toDateString()) return t('Today');
  return date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
}

function Thread({ me, id, onChanged }: { me: Me; id: string; onChanged: () => void }) {
  const detail = useLoad(() => api.getText(id), [id]);
  const { onText } = useNotifications();
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const conversation = detail.data;

  // New texts in it, from anyone: shown at once.
  useEffect(() => onText((event) => event.conversationId === id && detail.reload()), [id, onText, detail.reload]);
  // Opened: read, for everyone.
  useEffect(() => {
    if (conversation && conversation.unread > 0) void api.markTextRead(conversation.id).then(onChanged);
  }, [conversation?.id, conversation?.unread]);
  // In braces: browsers now return a promise from scrollIntoView, which React would take for a clean-up.
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [conversation?.messages.length]);

  async function reply(body: string): Promise<boolean> {
    setError(undefined);
    try {
      const message = await api.replyText(id, { body });
      detail.setData((current: TextConversationDetail | undefined) => current && { ...current, messages: [...current.messages, message] });
      onChanged();
      return message.status !== 'FAILED';
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Something went wrong. Try again.'));
      return false;
    }
  }

  if (detail.error) return <Notice kind="error">{detail.error}</Notice>;
  if (!conversation) return <Loading />;
  let lastDay = '';
  return (
    <div className="text-thread" data-testid="text-thread">
      <div className="text-thread-head">
        <Link to="/texts" className="button ghost icon texts-back" aria-label={t('All texts')}>
          <ArrowLeft size={18} aria-hidden />
        </Link>
        <span className="grow">
          {conversation.contactId ? (
            <Link to={`/contacts/${conversation.contactId}`} className="plain-link text-thread-name">
              {conversation.contactName}
            </Link>
          ) : (
            <span className="text-thread-name tabular">{formatNumber(conversation.remote)}</span>
          )}
          <span className="subtle text-thread-sub">
            {conversation.contactId && <span className="tabular">{formatNumber(conversation.remote)} · </span>}
            <span className="tabular">{t('via {number}', { number: ours(conversation) })}</span>
          </span>
        </span>
        {!conversation.contactId && conversation.remote.startsWith('+') && (
          <button type="button" className="secondary small" onClick={() => setSaving(true)}>
            <UserPlus size={15} aria-hidden /> {t('Save contact')}
          </button>
        )}
        {conversation.remote.startsWith('+') && (
          <button type="button" className="secondary small" onClick={() => void navigate(`/?number=${encodeURIComponent(conversation.remote)}`)} title={t('Put the number in the dialer')}>
            <PhoneOutgoing size={15} aria-hidden /> {t('Call')}
          </button>
        )}
      </div>
      <div className="text-messages">
        {conversation.messages.map((message) => {
          const day = dayOf(message.createdAt);
          const heading = day !== lastDay;
          lastDay = day;
          return (
            <div key={message.id} className="text-message-row">
              {heading && <div className="text-day">{day}</div>}
              <Bubble message={message} />
            </div>
          );
        })}
        <div ref={end} />
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {conversation.sendBlocked === 'sender-name' ? (
        <p className="subtle text-blocked">{t('They texted from a name, not a number, so you can’t reply.')}</p>
      ) : conversation.sendBlocked === 'texts-off' ? (
        <p className="subtle text-blocked">{t('Texts are off for {number}. An admin can turn them on in Settings, Numbers.', { number: formatNumber(conversation.ourNumber) })}</p>
      ) : (
        <Composer key={id} onSend={reply} autoFocus />
      )}
      <Dialog open={saving} onClose={() => setSaving(false)} title={t('New contact')} wide>
        {saving && (
          <ContactForm
            me={me}
            contact={null}
            initialNumber={conversation.remote}
            onCancel={() => setSaving(false)}
            onSaved={() => {
              setSaving(false);
              detail.reload();
              onChanged();
            }}
          />
        )}
      </Dialog>
    </div>
  );
}

/** A text to someone: from one of the numbers this person may text from. */
function NewText({ onSent }: { onSent: (conversation: TextConversationDetail) => void }) {
  const [params] = useSearchParams();
  const numbers = useLoad(api.textNumbers);
  const [numberId, setNumberId] = useState('');
  const [to, setTo] = useState(params.get('to') ?? '');
  const [body, setBody] = useState('');
  const { submit, busy, error, fields } = useSubmit();
  const chosen = numberId || numbers.data?.[0]?.id || '';
  const tooLong = textLength(body.trim()).parts > TEXT_MAX_PARTS;
  const navigate = useNavigate();

  // Someone we already text with (from a contact or a voicemail): their conversation.
  useEffect(() => {
    const given = params.get('to');
    if (!given?.startsWith('+')) return;
    void api.listTexts({ remote: given }).then(([existing]) => existing && navigate(`/texts/${existing.id}`, { replace: true }));
  }, [params, navigate]);

  async function send(event: FormEvent) {
    event.preventDefault();
    const sent = await submit(startTextSchema, { numberId: chosen, to, body }, api.startText);
    if (sent) onSent(sent);
  }

  if (numbers.error) return <Notice kind="error">{numbers.error}</Notice>;
  if (!numbers.data) return <Loading />;
  if (numbers.data.length === 0) {
    return (
      <EmptyState icon={MessageSquarePlus} title={t('No number to text from')}>
        <p>{t('Texts go from one of the company’s numbers with texts turned on. Ask an admin to turn them on for your number.')}</p>
      </EmptyState>
    );
  }
  return (
    <form className="stack text-new" onSubmit={(event) => void send(event)} data-testid="text-new">
      <div className="text-thread-head">
        <Link to="/texts" className="button ghost icon texts-back" aria-label={t('All texts')}>
          <ArrowLeft size={18} aria-hidden />
        </Link>
        <span className="grow">
          <h2>{t('New text')}</h2>
          {numbers.data.length === 1 && <span className="subtle text-thread-sub">{t('From {number}', { number: formatNumber(numbers.data[0]!.e164) })}</span>}
        </span>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      <div className="form-grid">
        <Field label={tc('text', 'To')} error={fields.to} hint={t('Their number, as you would dial it')}>
          <input value={to} onChange={(event) => setTo(event.target.value)} inputMode="tel" autoComplete="off" autoFocus={!to} data-testid="text-to" />
        </Field>
        {numbers.data.length > 1 && (
          <Field label={tc('text', 'From')} error={fields.numberId}>
            <select value={chosen} onChange={(event) => setNumberId(event.target.value)} data-testid="text-from">
              {numbers.data.map((number) => (
                <option key={number.id} value={number.id}>
                  {number.label ? `${number.label} · ${formatNumber(number.e164)}` : formatNumber(number.e164)}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
      <Field label={t('Text')} error={fields.body}>
        <textarea rows={4} value={body} onChange={(event) => setBody(event.target.value)} autoFocus={Boolean(to)} data-testid="text-body" />
      </Field>
      <div className="row text-composer-foot">
        <Length body={body} />
        <button type="submit" disabled={busy || !to.trim() || !body.trim() || tooLong} data-testid="text-send">
          <Send size={15} aria-hidden /> {t('Send')}
        </button>
      </div>
    </form>
  );
}

/**
 * Texts: the conversations this person sees, newest first, beside the open
 * one. On a phone, one or the other.
 */
export function TextsPage({ me }: { me: Me }) {
  const { id } = useParams();
  const list = useLoad(() => api.listTexts());
  const { onText } = useNotifications();
  const navigate = useNavigate();
  const creating = id === 'new';

  useEffect(() => onText(() => list.reload()), [onText, list.reload]);

  return (
    <div className="stack">
      <PageHeader title={t('Texts')} description={t('Texts (SMS) to and from your numbers.')}>
        <Link to="/texts/new" className="button" data-testid="text-new-button">
          <MessageSquarePlus size={16} aria-hidden /> {t('New text')}
        </Link>
      </PageHeader>
      {list.error && <Notice kind="error">{list.error}</Notice>}
      <div className={`texts ${id ? 'has-open' : ''}`}>
        <div className="card texts-list" data-testid="text-list">
          {!list.data ? (
            <Loading />
          ) : list.data.length === 0 ? (
            <EmptyState icon={MessageSquare} title={t('No texts yet')}>
              <p>{t('When someone texts one of your numbers, it appears here.')}</p>
            </EmptyState>
          ) : (
            list.data.map((conversation) => (
              <Link
                key={conversation.id}
                to={`/texts/${conversation.id}`}
                className={`texts-item ${conversation.id === id ? 'active' : ''} ${conversation.unread > 0 ? 'unread' : ''}`}
                data-testid="text-conversation"
              >
                <span className="texts-item-top">
                  <span className="texts-item-name">{who(conversation)}</span>
                  <span className="texts-item-time">{when(conversation.lastMessageAt)}</span>
                </span>
                <span className="texts-item-preview">
                  {conversation.lastDirection === 'OUT' && <span className="subtle">{t('You:')} </span>}
                  {conversation.lastPreview}
                </span>
                <span className="texts-item-ours">
                  {t('via {number}', { number: ours(conversation) })}
                  {conversation.unread > 0 && <span className="texts-item-badge">{conversation.unread}</span>}
                </span>
              </Link>
            ))
          )}
        </div>
        <div className="card texts-open">
          {creating ? (
            <NewText
              onSent={(conversation) => {
                list.reload();
                void navigate(`/texts/${conversation.id}`, { replace: true });
              }}
            />
          ) : id ? (
            <Thread me={me} id={id} onChanged={list.reload} />
          ) : (
            <EmptyState icon={MessageSquare} title={t('Choose a conversation')}>
              <p>{t('Or start a new one with New text.')}</p>
            </EmptyState>
          )}
        </div>
      </div>
    </div>
  );
}

/** The unread count beside Texts in the menu. */
export function TextsBadge() {
  const { textsUnread } = useNotifications();
  if (!textsUnread) return null;
  return (
    <span className="nav-badge" aria-label={tp(textsUnread, '{n} unread', '{n} unread')} data-testid="texts-unread">
      {textsUnread > 99 ? '99+' : textsUnread}
    </span>
  );
}
